import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { updateSEO } from '../../utils/seo';

// ===== 執事資格制度 一覧ページ 全面最新化（Implementer B スコープ・基本資格区画が主眼） =====
// デザイン土台（型正典）: src/pages/about/overview/page.tsx / src/pages/home/page.tsx（紺#1a2942 / 真鍮金#a38e65 /
//   金差し色#d7b779 / BTN-08 2状態 / 金eyebrow→金差し色見出し→真鍮短罫 / リード3行+ / 装飾ディバイダー禁止 /
//   一覧はカード乱立でなくエディトリアル）。
// 事実正典（創作禁止・実データ変更禁止・出典path記録）:
//   [S-cur] 旧 src/pages/qualifications/page.tsx（三段階・比較表・専門資格・料金・フローの実データ）
//   [S-canon] mng-thebutler/00_事業説明.md L63（三段階資格 Associate/Professional/Master）
//   [S-overview] src/pages/about/overview/page.tsx（BTN08・セクション文法）
// 注記: 専門資格9種の「詳細ページ」は別担当（本ファイルでは一覧上のリンク導線のみ・実データ/リンク保持）。

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

// [S-cur] 基本資格 三段階（実データ）
const tiers = [
  {
    roman: 'I', name: 'Associate Butler', ja: 'アソシエイト・バトラー', level: '初級', price: '48,000',
    href: '/qualifications/associate-butler', img: '/images/generated/jba-fs-169-qualifications.jpg',
    lead: '執事の基礎知識とマナーを体系的に学ぶ入門資格。未経験のかたでも安心して挑戦できます。',
    facts: [['テキスト', '約100頁'], ['学習期間', '2〜4ヶ月'], ['受験資格', 'なし']],
    skills: ['基本マナー・プロトコル', 'テーブルサービス基礎', 'コミュニケーション技術'],
  },
  {
    roman: 'II', name: 'Professional Butler', ja: 'プロフェッショナル・バトラー', level: '中級', price: '78,000',
    href: '/qualifications/professional-butler', img: '/images/generated/jba-fs-170-qualifications.jpg',
    lead: '幅広い知識と経験をもとに、独立して質の高いサービスを提供できる段階の資格。',
    facts: [['テキスト', '約200頁'], ['学習期間', '4〜6ヶ月'], ['受験資格', 'Associate 保持']],
    skills: ['高度なサービス技術', '問題解決・判断力', 'リーダーシップ'],
  },
  {
    roman: 'III', name: 'Master Butler', ja: 'マスター・バトラー', level: '上級', price: '128,000',
    href: '/qualifications/master-butler', img: '/images/generated/jba-fs-171-qualifications-v7.jpg',
    lead: '卓越した技術と知識を有し、他の執事の指導や複雑な状況のマネジメントができる最高位資格。',
    facts: [['テキスト', '約300頁'], ['学習期間', '6〜8ヶ月'], ['受験資格', 'Professional 保持']],
    skills: ['マネジメント・統括力', '指導・教育能力', '戦略的思考・企画力'],
  },
];

// [S-cur] 専門資格（一覧上の導線・詳細ページは別担当。実データ/リンク保持）
const specialtyGroups: { label: string; price: string; items: { name: string; ja: string; href: string; desc: string; isNew?: boolean }[] }[] = [
  {
    label: '通常専門資格', price: '58,000',
    items: [
      { name: 'Dining Coordinator', ja: 'ダイニング・コーディネーター', href: '/qualifications/dining-coordinator', desc: '食事サービスの専門技能。テーブルセッティングから料理の提供、各国のマナーまで。' },
      { name: 'Travel Concierge', ja: 'トラベル・コンシェルジュ', href: '/qualifications/travel-concierge', desc: '旅行コーディネートの専門技能。旅行計画から現地サポートまで。' },
      { name: 'Business Manner', ja: 'ビジネス・マナー', href: '/qualifications/business-manner', desc: 'ビジネスマナーの専門技能。執事流ビジネスマナーと国際儀礼。' },
    ],
  },
  {
    label: '執事知識資格', price: '38,000',
    items: [
      { name: 'Afternoon Tea', ja: 'アフタヌーン・ティー', href: '/qualifications/afternoon-tea', desc: 'アフタヌーンティーの文化とマナー。英国式の正統なサービスを習得。', isNew: true },
      { name: 'Tea Sommelier', ja: 'ティー・ソムリエ', href: '/qualifications/tea-sommelier', desc: '紅茶の専門知識。茶葉の選び方から淹れ方まで分かりやすく。', isNew: true },
      { name: 'Butler Style & Uniform', ja: 'バトラー・スタイル＆ユニフォーム', href: '/qualifications/butler-style-uniform', desc: '執事の装いと美学。フォーマルウェアの知識と品格ある装い。', isNew: true },
    ],
  },
];

const included = [
  '専用テキスト教材（冊子）', 'オンライン動画レッスン', '実技練習用キット', '認定試験問題・解答用紙',
  '試験添削サービス', '合格時：認定証・認定カード', '執事サロン3時間無料体験', '送料込み',
];

const flow = [
  { n: '1', t: 'お申込み', d: '専用フォームからお申込み。お振込確認後、教材を発送します。' },
  { n: '2', t: '教材到着・学習開始', d: 'テキスト、練習キット、動画視聴用アクセスコード、執事サロン無料券が届きます。' },
  { n: '3', t: '試験問題に挑戦', d: '準備ができたら試験問題に解答。解答用紙を返送（申込から1年以内）。' },
  { n: '4', t: '合否判定', d: '80点以上で合格。合格者には認定証と認定カードを発行。' },
  { n: '5', t: '資格取得完了', d: '認定証到着で資格取得完了。履歴書に記載できます（有効期限なし）。' },
];

const notes = [
  '本資格は全日本執事協会が認定する民間資格です',
  '資格取得により就職・転職・収入を保証するものではありません',
  '知識習得と自己研鑽を目的とした学習プログラムです',
  '実際の就業には各雇用先の採用基準に従ってください',
  '再試験料：15,000円（不合格の場合、1年以内なら何度でも再挑戦可能）',
];

function SecHead({ roman, en, children }: { roman?: string; en: string; children: React.ReactNode }) {
  return (
    <div className="mb-9">
      <div className="-mx-5 sm:-mx-8 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 py-[18px]">
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">{roman ? `${roman}. ` : ''}{en}</span>
      </div>
      <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em]">{children}</h2>
    </div>
  );
}

export default function QualificationsPage() {
  useEffect(() => {
    updateSEO({
      title: '執事検定・執事資格の種類と取得方法｜三段階の資格制度 | 全日本執事協会',
      description: '全日本執事協会が認定する執事検定・執事資格の完全ガイド。初級 Associate Butler・中級 Professional Butler・上級 Master Butler の三段階に、食事・旅行・ビジネスマナー・紅茶などの専門資格。自宅学習・通信試験で、確かな執事のキャリアを段階的に築けます。',
      keywords: '執事検定,執事資格,Associate Butler,Professional Butler,Master Butler,専門資格,全日本執事協会,認定資格',
      canonical: 'https://butler-association.jp/qualifications',
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事資格制度' },
  ];

  return (
    <Layout>
      {/* ===== ヒーロー：全幅ビルボード。由来: overview ヒーロー文法 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-p-016-qual-practice.jpg"
          alt="執事資格制度。段階的に学ぶ執事検定と認定資格"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.36)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-5 sm:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Butler Certifications</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事検定・執事資格制度</span>
              <span className="block text-[#d7b779] text-[clamp(20px,2.4vw,30px)] mt-2">プロフェッショナルな執事への道</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会は、プロフェッショナルな執事を育成するための執事検定・資格制度を運営しています。初級 Associate Butler から中級 Professional Butler、上級 Master Butler までの三段階に、食事・旅行・ビジネスマナー・紅茶などの専門資格を整備。自宅学習と通信試験で、確かなキャリアを段階的に築けます。
            </p>
          </div>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          {/* --- I. 概要リード + 細罫スタッツ。由来: overview S1 --- */}
          <section>
            <SecHead en="Overview">段階的に、<span className="text-[#a38e65]">確かな執事</span>へ</SecHead>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>当協会が認定する執事資格は、国内外のホテル業界や富裕層のかたがたから評価を受けており、執事としてのキャリアを確実に築くための重要なステップとなっています。</p>
                <p>資格制度の柱は、総合的な技能を段階的に証明する「基本資格」の三段階です。飛び級での受験はできず、Associate Butler（初級）→ Professional Butler（中級）→ Master Butler（上級）の順に積み上げます。加えて、食事サービスや紅茶、ビジネスマナーなど、分野に特化した「専門資格」も取得できます。</p>
                <p>すべての資格は通信教育で学べ、全国どこからでも受講が可能です。全資格に、お台場の執事サロン3時間無料体験（30,000円相当）が付きます。</p>
              </div>
              <div className="lg:pt-1">
                <img
                  src="/images/generated/jba-fs-173-qualifications.jpg"
                  alt="お台場の執事サロン。学びと体験の拠点"
                  className="w-full h-52 lg:h-[240px] object-cover border border-[#a38e65]/30 mb-5"
                />
                <dl className="border-t border-[#e2ddd2] pt-5 space-y-4">
                  <div className="flex items-baseline justify-between">
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em]">基本資格</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3<span className="text-sm text-[#8f7a56] ml-1">段階</span></dd>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-[#e2ddd2] pt-4">
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em]">専門資格</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">6<span className="text-sm text-[#8f7a56] ml-1">種</span></dd>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-[#e2ddd2] pt-4">
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em]">共通特典</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-base text-[#1a2942] leading-tight pt-1">サロン3時間無料</dd>
                  </div>
                </dl>
              </div>
            </div>
          </section>

          {/* --- II. 基本資格＝三段階の階梯（エディトリアル・ステッパー）= 新バリエーション①
               由来注記: overview S5 縦タイムライン（年号→出来事）と S4 番号付きブロックの文法を合流させ、
               資格の「級→資格名・料金・技能・導線」を1行1段の階梯として組む。カード乱立を避けたエディトリアル行。
               トップIII の CARD-E は採用済のため、一覧では別型（左サムネイル＋番号＋料金強調の横組み）で構成。 --- */}
          <section>
            <SecHead roman="I" en="Core Certifications">総合力を示す、<span className="text-[#a38e65]">三段階の基本資格</span></SecHead>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-10">
              執事としての総合的な技能を段階的に証明する資格です。初級から上級へ、一段ずつ着実に。飛び級での受験はできません。まずは基礎を固め、実践、そして統括へと歩みを進めます。
            </p>
            <div className="space-y-8">
              {tiers.map((t) => (
                <div key={t.name} className="grid md:grid-cols-[300px_1fr] gap-6 md:gap-9 items-stretch border-t border-[#e2ddd2] pt-8">
                  <div className="relative">
                    <img src={t.img} alt={`${t.name}（${t.ja}）の学び`} className="w-full h-52 md:h-full object-cover border border-[#a38e65]/30" />
                    <span className="absolute top-0 left-0 bg-[#1a2942] text-[#d7b779] font-['Playfair_Display',serif] text-xl px-3 py-1.5 tracking-[0.06em]">{t.roman}</span>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 mb-3">
                      <div>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-tight">{t.name}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#8f7a56] tracking-[0.08em] mt-1">{t.ja}・{t.level}</p>
                      </div>
                      <div className="text-right">
                        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.16em] text-[10px] text-[#8f7a56]">Fee</span>
                        <span className="jba-display text-[#1a2942] text-[26px] sm:text-[28px] leading-none">{t.price}<span className="text-sm text-[#8f7a56] ml-1">円</span></span>
                      </div>
                    </div>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9] mb-4">{t.lead}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mb-5">
                      {t.skills.map((s) => (
                        <span key={s} className="flex items-center gap-1.5 font-['Noto_Serif_JP',serif] text-[13px] text-[#1a2942]">
                          <span className="text-[#a38e65] text-[11px]" aria-hidden="true">◆</span>{s}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                      <dl className="flex flex-wrap gap-x-7 gap-y-1">
                        {t.facts.map(([k, v]) => (
                          <div key={k} className="flex items-baseline gap-1.5">
                            <dt className="font-['Noto_Serif_JP',serif] text-[11px] text-[#8f7a56]">{k}</dt>
                            <dd className="font-['Noto_Serif_JP',serif] text-[13px] text-[#1a2942]">{v}</dd>
                          </div>
                        ))}
                      </dl>
                      <a href={t.href} className={`${BTN08_ON_LIGHT} ml-auto !px-8 !py-3 text-[13px]`}>詳細を見る</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* --- III. 基本資格 比較表（罫線テーブル）。由来: overview S2 罫線dl の表版 --- */}
          <section>
            <SecHead roman="II" en="Compare">三段階を、<span className="text-[#a38e65]">見比べる</span></SecHead>
            <div className="overflow-x-auto border-t-2 border-[#1a2942]">
              <table className="w-full min-w-[560px] border-collapse font-['Noto_Serif_JP',serif]">
                <thead>
                  <tr className="border-b border-[#e2ddd2]">
                    <th className="text-left py-3.5 pr-4 text-sm text-[#8f7a56] font-medium tracking-[0.06em]">項目</th>
                    <th className="text-center py-3.5 px-3 text-[15px] text-[#1a2942] font-medium">Associate</th>
                    <th className="text-center py-3.5 px-3 text-[15px] text-[#1a2942] font-medium">Professional</th>
                    <th className="text-center py-3.5 px-3 text-[15px] text-[#1a2942] font-medium">Master</th>
                  </tr>
                </thead>
                <tbody className="text-[14px] text-[#4a4638]">
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">対象</td><td className="py-3.5 px-3 text-center">入門者</td><td className="py-3.5 px-3 text-center">実務経験者向け</td><td className="py-3.5 px-3 text-center">指導者レベル</td></tr>
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">テキスト</td><td className="py-3.5 px-3 text-center">約100ページ</td><td className="py-3.5 px-3 text-center">約200ページ</td><td className="py-3.5 px-3 text-center">約300ページ</td></tr>
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">学習期間</td><td className="py-3.5 px-3 text-center">2〜4ヶ月</td><td className="py-3.5 px-3 text-center">4〜6ヶ月</td><td className="py-3.5 px-3 text-center">6〜8ヶ月</td></tr>
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">受験資格</td><td className="py-3.5 px-3 text-center">なし</td><td className="py-3.5 px-3 text-center">Associate 保持</td><td className="py-3.5 px-3 text-center">Professional 保持</td></tr>
                  <tr><td className="py-3.5 pr-4 text-[#8f7a56]">料金</td>
                    <td className="py-3.5 px-3 text-center"><span className="jba-display text-lg text-[#1a2942]">48,000<span className="text-xs text-[#8f7a56]">円</span></span></td>
                    <td className="py-3.5 px-3 text-center"><span className="jba-display text-lg text-[#1a2942]">78,000<span className="text-xs text-[#8f7a56]">円</span></span></td>
                    <td className="py-3.5 px-3 text-center"><span className="jba-display text-lg text-[#1a2942]">128,000<span className="text-xs text-[#8f7a56]">円</span></span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* --- IV. 専門資格（エディトリアルリスト・料金グループ）= 新バリエーション②
               由来注記: overview S4 番号付きブロックを、料金帯ごとの見出し＋罫線行リストへ翻案。
               詳細ページは別担当のため、ここは分野名・料金・要約・導線のみ（実データ保持）。カード乱立を避けた行組み。 --- */}
          <section>
            <SecHead roman="III" en="Specialized">分野を極める、<span className="text-[#a38e65]">専門資格</span></SecHead>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              基本資格とは別に、特定分野の専門知識を証明する資格です。複数を組み合わせることで、より幅広いサービスを提供できる執事を目指せます。受験資格は特に設けていませんが、Associate Butler 以上の取得をおすすめします。
            </p>
            <div className="space-y-10">
              {specialtyGroups.map((g) => (
                <div key={g.label}>
                  <div className="flex items-baseline gap-4 mb-4 pb-3 border-b border-[#e2ddd2]">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{g.label}</h3>
                    <span className="jba-display text-[#a38e65] text-xl">{g.price}<span className="text-xs text-[#8f7a56] ml-0.5">円</span></span>
                    <span className="font-['Noto_Serif_JP',serif] text-[11px] text-[#8f7a56] tracking-[0.06em]">合格基準 80点以上</span>
                  </div>
                  <ul>
                    {g.items.map((it) => (
                      <li key={it.href} className="border-b border-[#e2ddd2] last:border-b-0">
                        <a href={it.href} className="flex flex-wrap items-center gap-x-5 gap-y-1.5 py-4 group hover:bg-[#f8f6f0] transition-colors duration-300 -mx-2 px-2">
                          <div className="min-w-[220px]">
                            <span className="jba-display text-[#1a2942] text-base sm:text-lg">{it.name}
                              {it.isNew && <span className="ml-2 align-middle font-['Playfair_Display',serif] text-[10px] tracking-[0.14em] text-[#8f7a56] border border-[#a38e65]/50 px-1.5 py-0.5">NEW</span>}
                            </span>
                            <span className="block font-['Noto_Serif_JP',serif] text-[11px] text-[#8f7a56] tracking-[0.06em] mt-0.5">{it.ja}</span>
                          </div>
                          <p className="flex-1 min-w-[240px] font-['Noto_Serif_JP',serif] text-[13px] text-[#4a4638] leading-[1.8]">{it.desc}</p>
                          <span className="font-['Noto_Serif_JP',serif] text-sm text-[#a38e65] shrink-0 group-hover:translate-x-1 transition-transform">詳細を見る →</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* --- V. 料金に含まれるもの（細罫チェックリスト）。由来: overview S2 罫線dl の一覧版 --- */}
          <section>
            <SecHead roman="IV" en="What's Included">料金に、<span className="text-[#a38e65]">すべて含まれます</span></SecHead>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 border-t-2 border-[#1a2942]">
              {included.map((x) => (
                <li key={x} className="flex items-start gap-2.5 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1" aria-hidden="true">◆</span>
                  <span className="font-['Noto_Serif_JP',serif] text-[14px] text-[#1a2942] leading-[1.7]">{x}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* --- VI. 資格取得の流れ（縦タイムライン細罫型）= 新バリエーション③。由来注記: overview S5 タイムライン文法 --- */}
          <section>
            <SecHead roman="V" en="How to Apply">資格取得までの、<span className="text-[#a38e65]">5ステップ</span></SecHead>
            <ol className="relative border-l border-[#e2ddd2] ml-1 max-w-3xl">
              {flow.map((s) => (
                <li key={s.n} className="relative pl-7 sm:pl-9 pb-8 last:pb-0">
                  <span className="absolute -left-[6px] top-1 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                  <div className="flex items-baseline gap-3 mb-1.5">
                    <span className="font-['Playfair_Display',serif] text-xl text-[#1a2942]">{s.n}</span>
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-lg">{s.t}</span>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* --- VII. ご確認ください（注意事項）。由来: overview 罫線dl の注記版 --- */}
          <section>
            <SecHead en="Please Note">ご確認ください</SecHead>
            <ul className="border-t border-[#e2ddd2] max-w-3xl">
              {notes.map((n) => (
                <li key={n} className="flex items-start gap-3 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1.5" aria-hidden="true">◆</span>
                  <span className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* --- VIII. 締めCTA（紺パネル）。由来: overview S6 --- */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-3">Next Steps</span>
              <h2 className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-3xl leading-[1.5] mb-5">あなたに最適な資格を、ご一緒に</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                キャリア目標や関心のある分野に応じて、最適な資格取得プランをご提案します。通信教育の詳しい進め方もあわせてご覧ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/qualifications/distance-learning" className={BTN08_ON_NAVY}>通信教育について見る</a>
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・相談する</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
